import { useCallback, useEffect, useRef, useState } from 'react';
import { notification, Typography } from 'antd';
import { useSelector } from 'react-redux';
import moment from 'moment';
import axios from 'axios';

import { IContaACadastrar } from './interfaces';
import { stateGlobal } from '../../../interfaces/Global';

import { mask } from '../../../utils/MascaraDeCampos';
import { useGetColumnSearchPropsData } from '../../../hooks/FilterTable/date';
import { useGetColumnSearchProps } from '../../../hooks/FilterTable/stringCLearFiltros';
import { useGetColumnSearchPropsObj } from '../../../hooks/FilterTable/objString';
import { convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../services/Conversores';
import { onlyNumbers } from '../utils';
import ContasACadastrarService from '../../../services/http/services/SaldoDeContas/ContasACadastrarService';
import { EmitirErro } from '../../../services/EmitirErro';

const { Text } = Typography;

function aplicarSinalSaldoAoValor(sinal: string, valor: number) {
    return ((sinal === "D" ? -1 : 1) * valor);
}

export const useContasACadastrar = () => {
    const propsState = useSelector((state: stateGlobal) => state.global);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [contasACadastrar, setContasACadastrar] = useState<IContaACadastrar[]>([]);
    const [hasSelectedCadastrar, setHasSelectedCadastrar] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [loadingIgnorar, setLoadingIgnorar] = useState(false);
    const [contaSelecionada, setContaSelecionada] = useState<IContaACadastrar | null>(null);

    const refContagemDoFiltro = useRef<Array<any>>([]);

    const signal = axios.CancelToken.source();
    const signal1 = axios.CancelToken.source();

    const buscaDadosContasACadastrar = useCallback((): void => {
        setLoadingTable(true);

        const buscaDeDadosUsuarios = async () => {
            try {
                const data = await ContasACadastrarService.getContasACadastrar(signal.token);

                setContasACadastrar(data);
                setLoadingTable(false)
            } catch (error) {
                let msgErro: any = (error as Error);
                if (msgErro?.message != 'Requisicao cancelada!') {
                    setLoadingTable(false)
                    notification.error({
                        message: 'Erro',
                        description:
                            'Não foi possivel buscar os dados!',
                    });
                }
            }
        }

        buscaDeDadosUsuarios();
    }, []);

    useEffect(() => {
        buscaDadosContasACadastrar();

        return () => {
            signal.cancel("Requisicao cancelada!");
            signal1.cancel("Requisicao cancelada!");
        }
    }, []);

    const clickBotaoCadastrarConta = useCallback((): void => {
        const buscaDeDados = () => {

            if (selectedRowKeys.length == 1) {
                const conta = contasACadastrar.find((e) => e.id === selectedRowKeys[0]);

                if (conta) {
                    setContaSelecionada(conta);
                }
            }
        }

        buscaDeDados();
    }, [selectedRowKeys, contasACadastrar]);

    const onclickTable = useCallback((record: IContaACadastrar, row: any): void => {

        if (selectedRowKeys.includes(record.id)) {
            let filter = selectedRowKeys.filter((val) => {
                return val != record.id
            })
            if (filter.length === 1) {
                setSelectedRowKeys(filter);
                setHasSelectedCadastrar(true);
            } else if (filter.length === 0) {
                setSelectedRowKeys(filter);
                setHasSelectedCadastrar(false);
            } else {
                setSelectedRowKeys(filter);
            }

        } else {
            if (selectedRowKeys.length == 0) {
                setSelectedRowKeys([record.id]);
                setHasSelectedCadastrar(true);
            } else {
                setSelectedRowKeys([record.id]);
                setHasSelectedCadastrar(true);
            }
        }
    }, [selectedRowKeys]);

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setHasSelectedCadastrar(false);
    }, []);

    const handleContaCreated = useCallback((_, contaACadastrarId: number | undefined) => {
        setContasACadastrar((prevState) => prevState.filter((conta) => conta.id !== contaACadastrarId));
        handleResetRowSelection();
        window.dispatchEvent(new Event('atualizarContagemBalon'));
    }, []);

    const onSelectChange = useCallback((selectedRowKeys): void => {
        if (selectedRowKeys.length > 1) {
            setSelectedRowKeys([selectedRowKeys[1]]);
            setHasSelectedCadastrar(true);
        } else if (selectedRowKeys.length === 0) {
            setSelectedRowKeys(selectedRowKeys);
            setHasSelectedCadastrar(false);
        } else {
            setSelectedRowKeys([selectedRowKeys[0]]);
            setHasSelectedCadastrar(true);
        }
    }, [selectedRowKeys]);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: '1%',
        type: 'radio' as any
    };

    const refFuncaoLimparFiltrosStringAgencia = useRef<any>(null);
    const refFuncaoLimparFiltrosStringNumero = useRef<any>(null);
    const refFuncaoLimparFiltrosStringSaldo = useRef<any>(null);
    const columnSearchPropsAgencia = useGetColumnSearchProps('agencia', refFuncaoLimparFiltrosStringAgencia);
    const columnSearchPropsNumero = useGetColumnSearchProps('numero', refFuncaoLimparFiltrosStringNumero);
    const columnSearchPropsSaldo = useGetColumnSearchProps('valorSaldo', refFuncaoLimparFiltrosStringSaldo);

    const columns = [
        {
            title: 'Órgão',
            dataIndex: 'id',
            ...useGetColumnSearchPropsObj('orgaoVinculado', "orgaoNome"),
            width: '4%',
            render: (_: any, record: IContaACadastrar) => record.orgaoVinculado.orgao.nome,
        },
        {
            title: 'Órgão vinculado',
            dataIndex: 'sinalSaldo',
            ...useGetColumnSearchPropsObj('orgaoVinculado', "nome"),
            width: '4%',
            render: (_: any, record: IContaACadastrar) => record.orgaoVinculado.nome,
        },
        {
            title: 'CNPJ',
            dataIndex: 'orgaoVinculado',
            ...useGetColumnSearchPropsObj('orgaoVinculado', "cnpj", onlyNumbers),
            width: '4%',
            render: (orgaoVinculado: any) => {
                const maskedValue = mask(orgaoVinculado.cnpj, [
                    "99.999.999/9999-99"
                ]);

                return maskedValue
            },
        },
        {
            title: 'Banco',
            dataIndex: 'banco',
            ...useGetColumnSearchPropsObj('banco', "nome"),
            width: '4%',
            render: (banco: any) => banco.nome,
        },
        {
            title: 'Agência',
            dataIndex: 'agencia',
            ...columnSearchPropsAgencia,
            width: '4%',
            render: (agencia: any) => agencia,
        },
        {
            title: 'Número',
            dataIndex: 'numero',
            ...columnSearchPropsNumero,
            width: '4%',
            render: (numero: any) => numero,
        },
        {
            title: 'Saldo',
            dataIndex: 'valorSaldo',
            ...columnSearchPropsSaldo,
            width: '4%',
            sorter: (a: IContaACadastrar, b: IContaACadastrar) => aplicarSinalSaldoAoValor(a.sinalSaldo, a.valorSaldo) - aplicarSinalSaldoAoValor(b.sinalSaldo, b.valorSaldo),
            render: (saldo: number, record: IContaACadastrar) => (
                <Text
                    style={{ color: record.sinalSaldo === "D" ? '#EB5757' : undefined }}
                >
                    {`R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(aplicarSinalSaldoAoValor(record.sinalSaldo, saldo), true)}`}
                </Text>
            ),
        },
        {
            title: 'Data saldo',
            dataIndex: 'dataSaldo',
            ...useGetColumnSearchPropsData('dataSaldo'),
            width: '4%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.dataSaldo) {
                    aMonMen = moment(a.dataSaldo).unix()
                } else {
                    aMonMen = 0
                }
                if (b.dataSaldo) {
                    bMonMen = moment(b.dataSaldo).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (dataSaldo: any) => (
                moment(dataSaldo).format('DD/MM/YYYY HH:mm')
            ),
        },
        {
            title: 'Criado em',
            dataIndex: 'createdAt',
            ...useGetColumnSearchPropsData('createdAt'),
            width: '4%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.createdAt) {
                    aMonMen = moment(a.createdAt).unix()
                } else {
                    aMonMen = 0
                }
                if (b.createdAt) {
                    bMonMen = moment(b.createdAt).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (criado: any) => (
                moment(criado).format('DD/MM/YYYY HH:mm')
            ),
        },
    ];

    const handleCloseContaForm = useCallback(() => {
        setContaSelecionada(null);
    }, []);

    const clickBotaoIgnorarConta = useCallback(async (): Promise<void> => {
        if (selectedRowKeys.length !== 1) return;

        const conta = contasACadastrar.find((e) => e.id === selectedRowKeys[0]);

        if (!conta) return;

        try {
            setLoadingIgnorar(true);

            await ContasACadastrarService.ignorarConta({
                numero: conta.numero,
                agencia: conta.agencia,
                bancoId: conta.bancoId,
                orgaoVinculadoId: conta.orgaoVinculadoId,
            });

            setContasACadastrar((prevState) => prevState.filter((item) => item.id !== conta.id));
            handleResetRowSelection();

            window.dispatchEvent(new Event('atualizarContagemBalon'));

            notification.success({
                message: 'Sucesso',
                description: 'Conta ignorada com sucesso!',
            });
        } catch (error) {
            EmitirErro(error);
        } finally {
            setLoadingIgnorar(false);
        }
    }, [selectedRowKeys, contasACadastrar, handleResetRowSelection]);

    return {
        clickBotaoCadastrarConta,
        clickBotaoIgnorarConta,
        hasSelectedCadastrar,
        loadingIgnorar,
        loadingTable,
        onclickTable,
        rowSelection,
        contasACadastrar,
        columns,
        refContagemDoFiltro,
        propsState,
        handleResetRowSelection,
        contaSelecionada,
        handleCloseContaForm,
        handleContaCreated,
    }
}
